import is from '@sindresorhus/is';
import { useSnackbar } from 'notistack';
import type { FunctionComponent } from 'react';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useDebounceValue } from 'usehooks-ts';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import { ConfirmationDialog } from '~/ui/core/confirmation-dialog/ConfirmationDialog';
import { OutlinedInputWrapper, SearchInput } from '~/ui/domain/search-input/SearchInput';

import { useConfirm } from '../../../../job-architecture-v2/providers/ConfirmProvider';
import { ActionsDropdown } from './ActionsDropdown';
import { DocumentsTable } from './DocumentsTable';
import { FileDropZone } from './FileDropZone';
import type { FileToUpload } from './types';
import { useDeleteDocument, useDocuments, useDownloadDocument, useGetUploadUrls } from './use-documents';
import { useDocumentsTable } from './use-documents-table';

const MIN_SEARCH_LENGTH = 3;
const DEBOUNCE_MS = 300;

export const DocumentsUploadSection: FunctionComponent = () => {
    const { applicationState, realtimeEventListenerBuilder } = useApplicationContext();
    const translate = useTranslate();
    const userEmail = applicationState.myUserProfile.get('emailAddress').unwrapOr('');
    const { enqueueSnackbar } = useSnackbar();
    const { confirm } = useConfirm();

    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery] = useDebounceValue(searchQuery, DEBOUNCE_MS);
    const searchTerm = debouncedSearchQuery.length >= MIN_SEARCH_LENGTH ? debouncedSearchQuery : undefined;

    const { deleteDocument, deleteDocuments } = useDeleteDocument();
    const { downloadDocument, downloadDocuments } = useDownloadDocument();
    const { getUploadUrls } = useGetUploadUrls();
    const { data, isLoading, refetch } = useDocuments({ searchTerm });

    const [pendingDocuments, setPendingDocuments] = useState<Map<string, File>>(new Map());
    const [isDownloadDialogOpen, setIsDownloadDialogOpen] = useState(false);

    const documents = data ?? [];

    const onDeleteDocument = useCallback(
        async (id: string, name: string) => {
            try {
                await confirm({
                    description: (
                        <Fragment>
                            {translate('pages.job-architecture-v2.knowledge-base.delete-document-description')}
                            <ul>
                                <li>{name}</li>
                            </ul>
                        </Fragment>
                    ),
                    title: translate('pages.job-architecture-v2.knowledge-base.delete-document-title'),
                    type: 'destructive',
                });
            } catch {
                return;
            }

            if (is.nonEmptyString(id)) {
                try {
                    await deleteDocument(id);
                } catch {
                    enqueueSnackbar(translate('pages.job-architecture-v2.knowledge-base.failed-to-delete-document'), {
                        variant: 'error',
                    });
                }
            }
        },
        [confirm, deleteDocument, enqueueSnackbar, translate],
    );

    const onDownloadDocument = useCallback(
        async (documentId: string) => {
            try {
                await downloadDocument(documentId);
            } catch {
                enqueueSnackbar(translate('pages.job-architecture-v2.knowledge-base.failed-to-download-document'), {
                    variant: 'error',
                });
            }
        },
        [downloadDocument, enqueueSnackbar, translate],
    );

    const { clearSelection, selectedDocuments, table } = useDocumentsTable({
        documents,
        onDelete: onDeleteDocument,
        onDownload: onDownloadDocument,
    });

    const removePendingDocument = useCallback((name: string) => {
        setPendingDocuments((pendingDocuments) => {
            const map = new Map(pendingDocuments);
            map.delete(name);

            return map;
        });
    }, []);

    useEffect(() => {
        const listener = realtimeEventListenerBuilder.build({
            method: 'GET',
            path: '/v1/events/tenant',
        });

        listener.addEventListener('notification', async (event: MessageEvent) => {
            const eventData = JSON.parse(event.data as string) as {
                payload: {
                    type: string;
                };
            };

            if (eventData.payload.type === 'DOCUMENT_UPLOAD') {
                const { document } = (
                    eventData as unknown as {
                        payload: {
                            document: {
                                id: string;
                                name: string;
                                status: string;
                                updatedAt: string;
                            };
                        };
                    }
                ).payload;

                await refetch();
                removePendingDocument(document.name);
            }
        });

        listener.start();

        return () => {
            listener.stop();
        };
    }, [realtimeEventListenerBuilder, refetch, removePendingDocument]);

    const uploadFilesToS3 = useCallback(
        async (nameFileToUploadMap: Map<string, FileToUpload>) => {
            await Promise.all(
                [...nameFileToUploadMap.entries()].map(async ([name, fileToUpload]) => {
                    try {
                        const response = await fetch(fileToUpload.presignedUrl, {
                            body: fileToUpload.file,
                            method: 'PUT',
                        });
                        if (!response.ok) {
                            throw new Error(`Upload failed with status ${response.status}`);
                        }
                    } catch {
                        enqueueSnackbar(
                            translate('pages.job-architecture-v2.knowledge-base.failed-to-upload-file', { name }),
                            { variant: 'error' },
                        );
                        removePendingDocument(name);
                    }
                }),
            );
        },
        [enqueueSnackbar, removePendingDocument, translate],
    );

    const onBulkDelete = useCallback(async () => {
        if (selectedDocuments.length === 0) {
            return;
        }

        try {
            await confirm({
                description: (
                    <Fragment>
                        {translate('pages.job-architecture-v2.knowledge-base.delete-document-description')}
                        <ul>
                            {selectedDocuments.map((doc) => (
                                <li key={doc.id}>{doc.name}</li>
                            ))}
                        </ul>
                    </Fragment>
                ),
                title: translate('pages.job-architecture-v2.knowledge-base.delete-document-title'),
                type: 'destructive',
            });
        } catch {
            return;
        }

        const documentIds = selectedDocuments.filter((doc) => is.nonEmptyString(doc.id)).map((doc) => doc.id);
        try {
            await deleteDocuments(documentIds);
            clearSelection();
        } catch {
            enqueueSnackbar(translate('pages.job-architecture-v2.knowledge-base.failed-to-delete-documents'), {
                variant: 'error',
            });
        }
    }, [clearSelection, confirm, deleteDocuments, enqueueSnackbar, selectedDocuments, translate]);

    const onBulkDownload = useCallback(async () => {
        if (selectedDocuments.length === 0) {
            return;
        }

        const documentIds = selectedDocuments.filter((doc) => is.nonEmptyString(doc.id)).map((doc) => doc.id);
        try {
            await downloadDocuments(documentIds);
            setIsDownloadDialogOpen(true);
        } catch {
            enqueueSnackbar(translate('pages.job-architecture-v2.knowledge-base.failed-to-download-documents'), {
                variant: 'error',
            });
        }
    }, [downloadDocuments, enqueueSnackbar, selectedDocuments, translate]);

    const handleFileSelection = useCallback(
        async (files: Map<string, File>) => {
            setPendingDocuments(() => {
                const newPendingDocuments = new Map<string, File>();
                for (const [fileName, file] of files) {
                    newPendingDocuments.set(fileName, file);
                }

                return newPendingDocuments;
            });
            try {
                const fileNames = [...files.keys()];
                const response = await getUploadUrls(fileNames);

                const fileUrlMap = new Map<string, FileToUpload>();
                for (const [fileName, url] of Object.entries(response)) {
                    const file = files.get(fileName);
                    if (file !== undefined) {
                        fileUrlMap.set(fileName, { file, presignedUrl: url });
                    }
                }
                await uploadFilesToS3(fileUrlMap);
            } catch {
                enqueueSnackbar(translate('pages.job-architecture-v2.knowledge-base.failed-to-obtain-upload-urls'), {
                    variant: 'error',
                });
                setPendingDocuments(new Map());
            }
        },
        [enqueueSnackbar, getUploadUrls, translate, uploadFilesToS3],
    );

    const hasDocuments = documents.length > 0;

    const closeDownloadDialog = useCallback(() => {
        setIsDownloadDialogOpen(false);
        clearSelection();
    }, [clearSelection]);

    return (
        <Fragment>
            <div className="[&>div]:bg-white-200 [&>div]:rounded-xl [&>div]:mb-0 [&>div]:mt-8xl [&>div]:py-8xl [&>div]:px-4xl">
                <FileDropZone isUploadInProgress={pendingDocuments.size > 0} onSelectingFiles={handleFileSelection} />
            </div>
            {(hasDocuments || isLoading || searchTerm !== undefined) && (
                <section>
                    <div className="flex items-center justify-between mb-xl mt-6xl">
                        <h4 className="text-sm font-medium text-black-700 m-0">
                            {translate('pages.job-architecture-v2.knowledge-base.table-title')}
                        </h4>
                        <div className="flex items-center gap-m">
                            <OutlinedInputWrapper>
                                <SearchInput
                                    hotkey="K"
                                    hotkeyScope="knowledge-base"
                                    placeholder={translate(
                                        'pages.job-architecture-v2.knowledge-base.search-placeholder',
                                    )}
                                    value={searchQuery}
                                    onChange={(event) => setSearchQuery(event.target.value)}
                                />
                            </OutlinedInputWrapper>
                            <ActionsDropdown
                                disabled={selectedDocuments.length === 0}
                                onDelete={onBulkDelete}
                                onDownload={onBulkDownload}
                            />
                        </div>
                    </div>
                    <DocumentsTable isLoading={isLoading} table={table} />
                </section>
            )}
            <ConfirmationDialog
                cancelButtonLabel=""
                confirmButtonLabel={translate('pages.job-architecture-v2.knowledge-base.download-started-confirm')}
                confirmButtonVariant="default"
                description={translate('pages.job-architecture-v2.knowledge-base.download-started-description', {
                    email: userEmail,
                })}
                onCancel={closeDownloadDialog}
                onConfirm={closeDownloadDialog}
                open={isDownloadDialogOpen}
                renderCancelButton={() => null}
                title={translate('pages.job-architecture-v2.knowledge-base.download-started-title')}
            />
        </Fragment>
    );
};
